import { useState, useEffect } from 'react';
import { useSeller } from '@/components/layout/DashboardLayout';
import { useProfile } from '@/lib/profile-context';
import { sellersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

export default function HistoryPage() {
  const { hasSelection } = useProfile();
  const { currentSellerId, currentSeller, syncVersion, syncing } = useSeller();
  const [history, setHistory] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentSellerId) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      try {
        const [histRes, ovRes] = await Promise.all([
          sellersAPI.getHistory(currentSellerId),
          sellersAPI.getOverview(currentSellerId),
        ]);
        setHistory(histRes.data);
        setOverview(ovRes.data);
      } catch (err) {
        console.error('Failed to load history:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentSellerId, syncVersion]);

  if (loading || (hasSelection && syncing)) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const lastScore = history.length > 0 ? history[history.length - 1]?.new_score : 0;
  const firstScore = history.length > 0 ? history[0]?.previous_score : 0;
  const totalDelta = lastScore - firstScore;

  return (
    <div className="space-y-6" data-testid="history-page">
      <div>
        <h1 className="text-xl font-semibold">History / Pre-Emptive Actions</h1>
        <p className="text-sm text-muted-foreground">Historical record of enforcement risk score updates.</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border/80">
          <CardContent className="p-4">
            <p className="text-3xl font-semibold tabular-nums">{firstScore}</p>
            <p className="text-xs text-muted-foreground mt-1">First Record</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/80">
          <CardContent className="p-4">
            <p className="text-3xl font-semibold tabular-nums">{lastScore}</p>
            <p className="text-xs text-muted-foreground mt-1">Current Score</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/80">
          <CardContent className="p-4">
            <p className={cn('text-3xl font-semibold tabular-nums', totalDelta > 0 ? 'text-[hsl(0,84%,60%)]' : 'text-[hsl(142,72%,45%)]')}>
              {totalDelta > 0 ? '+' : ''}{totalDelta}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Delta / Score Increase</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/80">
          <CardContent className="p-4">
            <p className="text-sm font-semibold">High-Severity Signals</p>
            <p className="text-xs text-muted-foreground mt-1">Run Context</p>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      {overview?.trend_data && (
        <Card className="bg-card border-border/80" data-testid="risk-history-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Risk Score Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 250, minWidth: 200 }}>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={overview.trend_data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222, 16%, 16%)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(215, 14%, 72%)' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: 'hsl(215, 14%, 72%)' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'hsl(222, 28%, 7%)', border: '1px solid hsl(222, 16%, 16%)', borderRadius: '8px', fontSize: '12px' }} />
                  <ReferenceLine y={25} stroke="hsl(142, 72%, 45%)" strokeDasharray="3 3" strokeOpacity={0.3} />
                  <ReferenceLine y={50} stroke="hsl(45, 93%, 58%)" strokeDasharray="3 3" strokeOpacity={0.3} />
                  <ReferenceLine y={75} stroke="hsl(0, 84%, 60%)" strokeDasharray="3 3" strokeOpacity={0.3} />
                  <Line type="monotone" dataKey="score" stroke="hsl(188, 100%, 55%)" strokeWidth={2} dot={false} activeDot={{ r: 5, stroke: 'hsl(188, 100%, 55%)', fill: 'hsl(222, 34%, 5%)' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Table */}
      <Card className="bg-card border-border/80" data-testid="risk-history-timeline">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">History / Pre-Emptive Actions</CardTitle>
          <CardDescription className="text-xs">Historical record of enforcement risk score updates.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-border/60 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="text-xs font-semibold">Primary Driver</TableHead>
                  <TableHead className="text-xs font-semibold">Run Time</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Previous Score</TableHead>
                  <TableHead className="text-xs font-semibold text-right">New Score</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Delta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((entry) => (
                  <TableRow key={entry.id} className="hover:bg-muted/20">
                    <TableCell className="text-sm font-medium">{entry.primary_driver}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{entry.run_time}</TableCell>
                    <TableCell className="text-sm tabular-nums text-right">{entry.previous_score}</TableCell>
                    <TableCell className="text-sm tabular-nums text-right">{entry.new_score}</TableCell>
                    <TableCell className={cn('text-sm font-medium tabular-nums text-right', entry.delta > 0 ? 'text-[hsl(0,84%,60%)]' : 'text-[hsl(142,72%,45%)]')}>
                      {entry.delta > 0 ? '+' : ''}{entry.delta}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
